import { useEffect, useRef, useState } from "react";
import {
  AdminButton, AdminCard, AdminCardHeader, AdminEmptyState, AdminErrorBanner, AdminFormField,
  AdminLoadingState, AdminPageLayout, AdminPagination, AdminSelect, AdminStatusBadge,
  AdminTable, AdminTd, AdminTextInput, AdminTr,
} from "@/components/admin";
import { fetchAdminMembershipDetail, fetchAdminMemberships } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  membershipMoney, membershipTabs, type JsonValue, type MembershipDetail, type MembershipFilters,
  type MembershipPage, type MembershipRow, type MembershipTab,
} from "@/lib/membership";

const statuses: Record<MembershipTab, string[]> = {
  plans: [], subscriptions: ["ACTIVE", "PENDING", "CANCELLED", "EXPIRED", "GRACE_PERIOD", "ON_HOLD", "PAUSED", "REVOKED"],
  orders: ["PENDING", "PROCESSED", "CANCELED", "PENDING_REFUND", "PARTIALLY_REFUNDED", "REFUNDED"],
  tokens: [], events: ["RECEIVED", "PROCESSING", "COMPLETED", "RETRYABLE", "FAILED"],
};
const headers: Record<MembershipTab, string[]> = {
  plans: ["플랜·상품", "연결 한도", "사용", "캐시 상태", "최근 동기화", "상세"],
  subscriptions: ["회원·플랜", "상품·기본 요금제", "구독 상태", "이용 권한", "자동 갱신·체험", "환경", "기간·검증"],
  orders: ["주문·회원", "상품", "주문 상태", "결제·세금·환불", "환경", "동기화·실패", "상세"],
  tokens: ["회원·최근 주문", "상품", "검증 당시 구독", "검증", "환경", "승인·실패", "상세"],
  events: ["이벤트·스토어", "처리 상태", "재시도", "실패 코드", "수신", "처리 완료", "다음 재시도"],
};
const labels: Record<string, string> = {
  basePlans: "기본 요금제", basePlanId: "기본 요금제 ID", offers: "혜택", offerId: "혜택 ID",
  regionalConfigs: "지역별 설정", regionCode: "국가", price: "가격", currencyCode: "통화",
  units: "정수 금액", nanos: "소수 금액(10억분의 1)", state: "상태", billingPeriodDuration: "결제 주기",
  autoRenewingBasePlanType: "자동 갱신 요금제", phases: "혜택 단계", free: "무료",
  total: "총 결제액", tax: "세금", orderHistory: "주문 이력", refundEvent: "전체 환불",
  partialRefundEvents: "부분 환불", refundAmount: "환불 금액", refundState: "환불 상태",
  orderId: "주문 번호", lineItems: "구매 항목", productId: "상품 ID",
  createTime: "생성 시각", lastEventTime: "최근 변경", eventTime: "발생 시각",
  subscriptionDetails: "구독 기간", servicePeriodStartTime: "서비스 시작", servicePeriodEndTime: "서비스 종료",
};
const Status = ({ value }: { value: string | null }) => <AdminStatusBadge label={value ?? "미수집"}
  tone={value === "FAILED" ? "danger" : value === "COMPLETED" || value === "VERIFIED" ? "success" : "neutral"} />;

function Snapshot({ value }: { value: JsonValue }) {
  if (value === null) return <span>미수집</span>;
  if (typeof value !== "object") return <span className="break-all">{String(value)}</span>;
  const entries = Array.isArray(value) ? value.map((v, i) => [String(i + 1), v] as const) : Object.entries(value);
  if (!entries.length) return <span>제공된 항목 없음</span>;
  return <dl className="space-y-2 border-l border-slate-200 pl-3">{entries.map(([key, entry]) =>
    <div key={key}><dt className="text-xs text-slate-500">{labels[key] ?? key}</dt>
      <dd className="mt-1 text-sm text-slate-700"><Snapshot value={entry} /></dd></div>)}</dl>;
}

export default function MembershipsPage() {
  const [tab, setTab] = useState<MembershipTab>("plans");
  const [draft, setDraft] = useState<MembershipFilters>({});
  const [filters, setFilters] = useState<MembershipFilters>({});
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<MembershipPage<MembershipRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<MembershipDetail | null>(null);
  const [catalog, setCatalog] = useState<{ name: string; value: JsonValue } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailSequence = useRef(0);
  useEffect(() => {
    let current = true;
    setLoading(true); setError(null); setData(null);
    void fetchAdminMemberships(tab, filters, page).then(result => {
      if (current) setData(result);
    }).catch((e: unknown) => {
      if (current) setError(e instanceof Error ? e.message : "조회에 실패했습니다.");
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [tab, filters, page, refresh]);
  useEffect(() => () => { detailSequence.current++; }, []);
  const closeDetail = () => {
    detailSequence.current++; setDetail(null); setCatalog(null); setDetailLoading(false); setDetailError(null);
  };
  const showDetail = async (id: number) => {
    const seq = ++detailSequence.current;
    setDetailLoading(true); setDetail(null); setDetailError(null);
    try {
      const result = await fetchAdminMembershipDetail(id);
      if (seq === detailSequence.current) setDetail(result);
    } catch (e) { if (seq === detailSequence.current) setDetailError(e instanceof Error ? e.message : "상세 조회 실패"); }
    finally { if (seq === detailSequence.current) setDetailLoading(false); }
  };
  const input = (key: keyof MembershipFilters, label: string, type = "text") =>
    <AdminFormField label={label} htmlFor={key} key={key}><AdminTextInput id={key} type={type}
      value={draft[key] ?? ""} onChange={e => setDraft({ ...draft, [key]: e.target.value })} /></AdminFormField>;
  const select = (key: keyof MembershipFilters, label: string, options: string[]) =>
    <AdminFormField label={label} htmlFor={key}><AdminSelect id={key} value={draft[key] ?? ""}
      onValueChange={v => setDraft({ ...draft, [key]: v })}><option value="">전체</option>
      {options.map(v => <option key={v} value={v}>{v}</option>)}</AdminSelect></AdminFormField>;
  const cells = (row: MembershipRow) => {
    if ("catalog" in row) return [
      <><strong>{row.displayName}</strong><div>{row.productId ?? "상품 미연결"}</div><div>{row.planCode}</div></>,
      row.maxConnectionCount === null ? "제한 없음" : row.maxConnectionCount, row.useYn,
      <><Status value={row.syncStatus} /><div>{row.failureCode}</div></>, formatDateTime(row.syncedAt),
      <AdminButton size="sm" variant="secondary" onClick={() => { closeDetail(); setCatalog({ name: row.displayName, value: row.catalog }); }}>상품 설정</AdminButton>,
    ];
    if ("entitlementActive" in row) return [
      <>{row.userAccountId ?? "탈퇴"}<div>{row.planCode}</div></>,
      <>{row.productId}<div>{row.basePlanId ?? "미제공"} / {row.offerId ?? "혜택 없음"}</div></>,
      <Status value={row.status} />, row.entitlementActive ? "활성" : "비활성",
      `${row.autoRenew ? "갱신" : "갱신 안 함"} · ${row.trial ? "체험" : "일반"}`, row.environment,
      <>{formatDateTime(row.purchasedAt)} ~ {formatDateTime(row.expiresAt)}<div>검증 {formatDateTime(row.verifiedAt)}</div></>,
    ];
    if ("type" in row) return [
      <>{row.type}<div>{row.storeType} · #{row.id}</div></>, <Status value={row.status} />, row.retryCount,
      row.failureCode ?? "-", formatDateTime(row.eventAt), formatDateTime(row.processedAt), formatDateTime(row.nextRetryAt),
    ];
    const sync = <><Status value={tab === "tokens" ? row.acknowledgementStatus : row.syncStatus} />
      <div>{row.failureCode ?? "-"}</div><div>재시도 {row.retryCount} · {formatDateTime(row.syncedAt)}</div></>;
    return [
      <>{row.orderId ?? "주문 번호 없음"}<div>회원 {row.userAccountId ?? "탈퇴"} · #{row.id}</div></>,
      <>{row.productId}<div>{row.basePlanId ?? "미제공"} / {row.offerId ?? "혜택 없음"}</div></>,
      <Status value={tab === "tokens" ? row.subscriptionStatusAtVerification : row.orderStatus} />,
      tab === "tokens" ? <Status value={row.verificationStatus} /> : <>
        <div>결제 {membershipMoney(row.paidAmount, row.currency)}</div><div>세금 {membershipMoney(row.taxAmount, row.currency)}</div>
        <div>환불 {membershipMoney(row.refundAmount, row.currency)}</div></>,
      row.environment, sync, <AdminButton size="sm" variant="secondary" onClick={() => void showDetail(row.id)}>조회 상세</AdminButton>,
    ];
  };
  return <AdminPageLayout title="멤버십·결제 조회" subtitle="스토어 검증과 주문 원장 · 읽기 전용"
    actions={<AdminButton variant="soft" disabled={loading} onClick={() => setRefresh(v => v + 1)}>새로고침</AdminButton>}>
    <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="조회 종류">
      {(Object.entries(membershipTabs) as [MembershipTab, string][]).map(([key, name]) =>
        <AdminButton key={key} variant={tab === key ? "primary" : "secondary"} aria-pressed={tab === key}
          onClick={() => { setTab(key); setDraft({}); setFilters({}); setPage(0); closeDetail(); }}>{name}</AdminButton>)}
    </div>
    <p className="mb-4 text-sm text-slate-500">수집된 기록만 표시합니다. 수집 시작 전 과거 주문 전체가 포함되지는 않을 수 있습니다.
      미확인 금액은 0원이 아닙니다. 모든 시각은 API의 한국 시각 기준입니다.</p>
    {tab !== "plans" && <AdminCard className="mb-4 p-4"><form onSubmit={e => {
      e.preventDefault();
      if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) { setError("시작일은 종료일 이전이어야 합니다."); return; }
      if (draft.userAccountId && !/^[1-9]\d*$/.test(draft.userAccountId)) { setError("회원 ID는 양의 정수로 입력하세요."); return; }
      setFilters({ ...draft }); setPage(0); closeDetail();
    }}><div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-4">
      {tab !== "events" && <>{input("productId", "상품 ID")}{input("userAccountId", "회원 ID")}
        {select("environment", "환경", ["PRODUCTION", "TEST", "SANDBOX", "DEV_SIMULATOR"])}</>}
      {(tab === "orders" || tab === "tokens") && <>{input("orderId", "주문 번호")}
        {select("syncStatus", "동기화 상태", ["PENDING", "RETRYABLE", "FAILED", "COMPLETED"])}</>}
      {statuses[tab].length > 0 && select("status", "상태", statuses[tab])}
      {input("fromDate", tab === "events" ? "수신 시작일" : "구매 시작일", "date")}
      {input("toDate", tab === "events" ? "수신 종료일" : "구매 종료일", "date")}
    </div><div className="mt-3 flex gap-2"><AdminButton type="submit" disabled={loading}>조회</AdminButton>
      <AdminButton type="button" variant="secondary" onClick={() => { setDraft({}); setFilters({}); setPage(0); closeDetail(); }}>초기화</AdminButton></div></form></AdminCard>}
    <AdminErrorBanner message={error} />
    {loading ? <AdminLoadingState /> : !error && data && <AdminCard className="overflow-hidden">
      <AdminCardHeader title={`${membershipTabs[tab]} · ${data.totalElements}건`} />
      {!data.items.length ? <AdminEmptyState title="조회된 기록이 없습니다." description="조회 조건과 수집 상태를 확인해 주세요." /> :
        <AdminTable headers={headers[tab]}>{data.items.map(row => <AdminTr key={row.id}>
          {cells(row).map((cell, i) => <AdminTd key={i}><div className="max-w-xs break-words text-xs">{cell}</div></AdminTd>)}
        </AdminTr>)}</AdminTable>}
      {tab !== "plans" && data.totalPages > 1 && <AdminPagination currentPage={page + 1} totalPages={data.totalPages}
        totalCount={data.totalElements} pageSize={50} onPageChange={p => { setPage(p - 1); closeDetail(); }} />}
    </AdminCard>}
    {(detail || catalog || detailLoading || detailError) && <AdminCard className="mt-4 p-4">
      <div className="mb-4 flex items-center justify-between"><h2 className="font-semibold">{catalog?.name ?? "주문·검증 상세"}</h2>
        <AdminButton variant="secondary" size="sm" onClick={closeDetail}>닫기</AdminButton></div>
      <AdminErrorBanner message={detailError} />{detailLoading && <AdminLoadingState />}
      {catalog && <Snapshot value={catalog.value} />}
      {detail && <><p className="mb-3 text-sm">주문 {detail.transaction.orderId ?? "없음"} · 다음 조회 {formatDateTime(detail.transaction.nextSyncAt)}</p>
        <Snapshot value={detail.orderSnapshot} /></>}
    </AdminCard>}
  </AdminPageLayout>;
}
